import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, LocateFixed, Search, Clock, ChevronDown, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLocationCtx, POPULAR_CITIES } from '@/contexts/LocationContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LocationSelector = ({ compact = false }) => {
  const { location, status, detect, setLocation, recent, dismissPrompt } = useLocationCtx();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/stats/cities`).then((r) => r.json()).then((d) => setCities(Array.isArray(d) ? d : d.cities || [])).catch(() => {});
  }, [open]);

  const allCities = Array.from(new Set([...POPULAR_CITIES, ...cities.map((c) => c.city || c.name || c)]));
  const filtered = allCities.filter((c) => c.toLowerCase().includes(q.toLowerCase()));
  const choose = (city) => { setLocation({ city, source: 'manual' }); setOpen(false); setQ(''); };

  return (
    <>
      <button onClick={() => setOpen(true)} className={`flex items-center gap-1.5 rounded-full border border-border/60 bg-white/80 hover:border-[#D4AF37] transition-colors ${compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} text-[#0F172A]`} data-testid="location-pill">
        <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
        <span className="font-medium max-w-[120px] truncate">{location?.city || (status === 'detecting' ? 'Detecting…' : 'Select City')}</span>
        <ChevronDown className="h-3 w-3 text-muted-foreground" />
      </button>

      {status === 'prompting' && !location && createPortal(
        <div className="fixed bottom-4 left-4 right-4 sm:right-auto sm:left-6 sm:w-[380px] z-[55] bg-[#0F172A] text-white rounded-2xl shadow-2xl p-4 flex gap-3" data-testid="location-permission-banner">
          <LocateFixed className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm">See properties near you</p>
            <p className="text-xs text-white/70 mt-0.5">Allow location access to show homes in your city first. You can change the city anytime.</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="btn-gold h-8" onClick={detect} data-testid="location-allow-btn">Use my location</Button>
              <Button size="sm" variant="ghost" className="h-8 text-white hover:bg-white/10" onClick={() => { dismissPrompt(); setOpen(true); }} data-testid="location-select-city-btn">Select city</Button>
            </div>
          </div>
          <button onClick={dismissPrompt} aria-label="Dismiss" data-testid="location-dismiss-btn"><X className="h-4 w-4 text-white/60" /></button>
        </div>, document.body
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Select Location</DialogTitle>
            <DialogDescription>Results and recommendations update instantly for the chosen city.</DialogDescription>
          </DialogHeader>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={() => { detect(); setOpen(false); }} data-testid="location-use-current">
            <LocateFixed className="h-4 w-4 text-[#D4AF37]" /> Use current location
          </Button>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="location-search-input" />
          </div>
          {recent.length > 0 && !q && (
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Clock className="h-3 w-3" /> Recent</p>
              <div className="flex flex-wrap gap-2">{recent.map((c) => <button key={c} onClick={() => choose(c)} className="px-3 py-1 rounded-full border text-sm hover:border-[#D4AF37]" data-testid={`location-recent-${c}`}>{c}</button>)}</div>
            </div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{q ? 'Matches' : 'Popular cities'}</p>
            <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
              {filtered.map((c) => (
                <button key={c} onClick={() => choose(c)} className={`text-left px-3 py-2 rounded-sm border text-sm hover:border-[#D4AF37] ${location?.city === c ? 'border-[#D4AF37] bg-[#D4AF37]/10' : ''}`} data-testid={`location-city-${c.replace(/\s+/g, '-')}`}>{c}</button>
              ))}
              {filtered.length === 0 && <p className="text-sm text-muted-foreground col-span-2">No city found</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
